import { useAuth } from '@agentnet/auth';
import { appHeaderHeight, pxToRem, topNavHeight } from '@fluentsms/agentnet-web-components';
import { CircularProgress, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { MarketplaceProduct } from 'api/admin/types';
import {
  GetFileInfoProps,
  getOrderInsightsEmailPreference,
  refreshFileOrderInsights,
  UpdateOrderInsightsEmailPreference,
} from 'api/file/file-api';
import {
  capitalizeFirstLetter,
  disclaimerText,
  filePartyTypeMap,
  getParentSeverity,
  onFileOwnerAddresses,
  onFileOwnerNames,
  OrderInsightJacket,
  Party,
  PartyOrderInsightsProps,
  Property,
  Risk,
  RiskCategory,
  toFullAddress,
} from 'api/file/interfaces/get-file-order-insights';
import { generateFileSummaryReport } from 'api/pdf/pdf-api';
import { KBLinks } from 'features/constants';
import {
  enhancedVerificationFailedRisk,
  enhancedVerificationPendingRisk,
  validationAlertRisk,
} from 'features/help/constants';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import useAsync from 'hooks/useAsync';
import { useMarketPlace } from 'hooks/useMarketPlace';
import { useViewState } from 'hooks/ViewStateContext';
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import AccordionContent from 'ui-kit/components/accordion/AccordionContent';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import AgentNetDivider from 'ui-kit/components/dividers/AgentNetDivider2';
import PageHeader from 'ui-kit/components/headers/PageHeader';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import ContactUsDialog from 'ui-kit/components/modal/ContactUsDialog';
import OrderInsightsEmailPreferenceDrawer, {
  OrderInsightsEmailPreference,
} from 'ui-kit/components/modal/OrderInsightsEmailPreferenceDrawer';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import PDFIcon from 'ui-kit/icons/PDF';
import { TransactionStatusEnum } from 'utilities/verification/types';
import { useFeatureFlag } from '@agentnet/feature-flags';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';
import EmptyImg from '../../../common-assets/images/empty.svg';
import PartyNotCreated from '../parties/PartyNotCreated/PartyNotCreated';
import OrderInsightUpdateButton from './components/OrderInsightUpdateButton';
import PartyOrderInsights from './PartyOrderInsights';
import PropertyOrderInsights from './PropertyOrderInsights';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    summaryWrap: {
      display: 'flex',
    },
    summaryWrapRow: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      alignItems: 'center',
    },
    iconWrap: {
      backgroundColor: 'transparent',
      borderRadius: '4px',
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: `0 ${theme.spacing(1)}px`,
    },
    discTrigger: {
      padding: '0 0 8px 0',
      '&:hover': {
        background: 'transparent',
      },
    },
    lowerCase: {
      textTransform: 'none',
    },
    lowerCase2: {
      textTransform: 'none',
      paddingBottom: theme.spacing(3),
    },
    truncate: {
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      minHeight: '21px',
      maxWidth: '500px',
    },
    subtitleWrap: {
      display: 'flex',
      alignItems: 'center',
      whiteSpace: 'nowrap',
    },
    subtitle: {
      color: theme.palette.text.secondary,
      marginRight: theme.spacing(2),
      maxWidth: '350px',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
    titleWrap: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      margin: `0 ${theme.spacing(1)}px`,
      height: '43px',
    },
    summaryActions: {
      marginLeft: 'auto',
      display: 'flex',
      alignItems: 'center',
      '& .MuiSvgIcon-root': {
        fontSize: pxToRem(21),
      },
    },
    summaryActionBtn: {
      marginLeft: pxToRem(12),
    },
    expandIcon: {
      margin: `0 ${theme.spacing(1)}px`,
      transform: 'scale(1.8) rotate(-90deg)',
      transition: `.156s all`,
      fill: theme.palette.text.secondary,
      alignSelf: 'center',
    },
    titleOnly: {
      lineHeight: pxToRem(28),
    },
    isEditing: {
      backgroundColor: '#FFF7EB',
      '& .MuiAccordionSummary-content': {
        backgroundColor: 'transparent',
      },
    },
    selectableText: {
      userSelect: 'text',
      cursor: 'text',
    },
    center: {
      textAlign: 'center',
      margin: theme.spacing(4, 0),
    },
    disclaimer: {
      display: 'flex',
      justifyContent: 'flex-start',
      gap: '16px',
      padding: theme.spacing(3, 0, 1, 0),
    },
    flex: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '16px',
      flexDirection: 'column',
      //margin: theme.spacing(3, 0),
    },
    flexGap: {
      display: 'flex',
      gap: '16px',
      alignItems: 'center',
    },
    flexRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginTop: theme.spacing(2),
    },
    container: {
      flex: '1',
      padding: theme.spacing(0, 3),
    },
    propertyType: {
      backgroundColor: '#0C6961',
    },
    buyerType: {
      backgroundColor: '#6B21A8',
    },
    sellerType: {
      backgroundColor: '#B63F0C',
    },
    lenderType: {
      backgroundColor: '#901949',
    },
    gIcon: {
      background: '#0C6961',
      color: '#fff',
      fontSize: theme.spacing(4),
      margin: theme.spacing(0, 2),
      padding: theme.spacing(1),
      borderRadius: theme.spacing(1),
    },
    pIcon: {
      background: '#6B21A8',
      color: '#fff',
      fontSize: theme.spacing(4),
      margin: theme.spacing(0, 2),
      padding: theme.spacing(1),
      borderRadius: theme.spacing(1),
    },
    rIcon: {
      background: theme.palette.error.main,
      color: '#fff',
      fontSize: theme.spacing(4),
      margin: theme.spacing(0, 2),
      padding: theme.spacing(1),
      borderRadius: theme.spacing(1),
    },
    drIcon: {
      background: '#901949',
      color: '#fff',
      fontSize: theme.spacing(4),
      margin: theme.spacing(0, 2),
      padding: theme.spacing(1),
      borderRadius: theme.spacing(1),
    },
    w100: {
      width: '100%',
    },
    warning: {
      color: theme.palette.error.main,
      fontSize: theme.spacing(2),
    },
    greyTxt: {
      color: theme.palette.text.secondary,
      fontSize: '14px',
      maxWidth: '90%',
    },
    success: {
      color: theme.palette.success.main,
      fontSize: theme.spacing(2),
    },
    link: {
      color: theme.palette.text.secondary,
      textDecoration: 'underline',
      cursor: 'pointer',
      '&:hover': {
        opacity: '0.8',
      },
    },
    loader: {
      minHeight: 'fit-content',
      marginLeft: theme.spacing(1),
    },
  }),
);

export default function OrderInsightsContent(): JSX.Element {
  const classes = useStyles();
  const { fileId } = useParams<GetFileInfoProps>();
  const location = useLocation();
  const partiesSectionRef = useRef<{
    [key: string]: { element: HTMLDivElement; accordionContent: HTMLElement } | null;
  }>({});
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { userFirm } = profileCtx;
  const history = useHistory();
  const { getAccessToken } = useAuth();
  const { addGlobalMsg } = useGlobalMessages();
  const { addSnackbarMessage } = useSnackBars();
  const { setRouterParamValue } = useViewState();
  const fileDataCtx: FileDataContextInterface = useContext(FileDataContext) ?? {};
  const {
    fileData,
    loadFileData,
    fileOrderInsightsData,
    fileRefreshTrigger,
    triggerFileRefresh,
    setFileOrderInsightsData,
    loadFileOrderInsightsData,
    isDiscoveringOrderInsights,
  } = fileDataCtx;
  const [properties, setProperties] = React.useState<Property[]>();
  const [parties, setParties] = React.useState<Party[]>();
  const [policyLiabilityJacket, setPolicyLiabilityJacket] = React.useState<OrderInsightJacket>();
  const [lastUpdated, setLastUpdated] = React.useState<string>('');
  const [isActionInProgress, setActionInProgress] = React.useState<string>('');
  const [orderInsightsError, setOrderInsightsError] = React.useState<string[]>([]);
  const [contactUsModal, setContactUsModal] = React.useState(false);
  const [enableStatus, setEnableStatus] = React.useState<PartyOrderInsightsProps['enableStatus']>({});
  const [downloadingSummary, setDownloadingSummary] = React.useState(false);
  const [emailPreferenceDrawer, setEmailPreferenceDrawer] = React.useState(false);
  const [updateEmailPreferenceInProgress, setUpdateEmailPreferenceInProgress] = React.useState(false);
  const [pendingScrollPartyId, setPendingScrollPartyId] = React.useState<string | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const showFincenInOI = useFeatureFlag(FeatureFlag.SHOW_FINCEN_IN_ORDER_INSIGHTS, false);

  const isFraudValidationDisabled = false;

  const defaultEmailPreference: OrderInsightsEmailPreference = useMemo(
    () => ({
      firmId: userFirm?.firmId ?? '',
      isEnabled: false,
      emailFor: { onlyMyFiles: true, offices: [] },
      notificationTypes: {
        insightsUnavailable: true,
        watchListProperty: true,
        watchListParty: true,
        claimProperty: true,
        claimParty: true,
        vacant: true,
        ownerName: true,
        foreign: true,
        nonOwner: true,
        ownerAddr: true,
        lowMarket: true,
        lowAssess: true,
        finCEN: true,
        authLimit: true,
        sdn: true,
        identityVerificationStatus: true,
      },
    }),
    [],
  );
  const [emailPreferenceDrawerData, setEmailPreferenceDrawerData] =
    React.useState<OrderInsightsEmailPreference>(defaultEmailPreference);

  const onEmailSubscribe = () => {
    if (updateEmailPreferenceInProgress || !emailPreferenceData) return;
    setEmailPreferenceDrawer(true);
  };

  const getEmailPreference = async (): Promise<OrderInsightsEmailPreference> => {
    const token = await getAccessToken();
    return getOrderInsightsEmailPreference(userFirm?.firmId ?? '', token);
  };

  const {
    execute: executeGetEmailPreference,
    status: emailPreferenceStatus,
    value: emailPreferenceData,
    errors: emailPreferenceError,
  } = useAsync<OrderInsightsEmailPreference>(getEmailPreference, false);

  const refreshOrderInsights = async (): Promise<boolean> => {
    const token = await getAccessToken();
    return refreshFileOrderInsights(fileId ?? '', token) as Promise<boolean>;
  };
  const {
    execute: executeRefreshOrderInsights,
    status: refreshOrderInsightsStatus,
    value: refreshOrderInsightsData,
    errors: refreshOrderInsightsError,
  } = useAsync<boolean>(refreshOrderInsights, false);

  const showErrorMessage = (errorMessage: string[]) => {
    errorMessage?.map((err) => {
      addGlobalMsg({
        message: err,
        type: 'error',
      });
    });
    setOrderInsightsError([]);
  };

  const { isMarketPlaceEnabled, fetchMarketPlaceServices, fetchMarketPlaceServicesResults } = useMarketPlace();

  const handleRefreshOrderInsights = () => {
    setActionInProgress('pending');
    executeRefreshOrderInsights().then();
  };

  const setPartyRef = useCallback(
    (id: number, key: string) => (el: HTMLDivElement | null) => {
      if (el) {
        // Find and store both the element and accordion content in one ref object
        // getting from callback ref for intersection observer to detect wether the accordian content
        // is open or not
        const accordionContent = el.querySelector('[role="region"]') || el.querySelector('.MuiCollapse-wrapperInner');
        if (accordionContent) {
          partiesSectionRef.current[`${id}-${key}`] = {
            element: el,
            accordionContent: accordionContent as HTMLElement,
          };
        }
      }
    },
    [],
  );

  const scrollToPartyByRef = useCallback(
    (partyId: string) => {
      if (!parties) return;

      // Find the first matching party
      const party = parties.find((p) => String(p.id) === String(partyId));
      if (!party) return;

      const refKey = `${party.id}-${party.key}`;
      const refData = partiesSectionRef.current?.[refKey];
      if (!refData) return;

      const { element, accordionContent } = refData;

      // Clean up any existing observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      // Create observer to detect when accordion is expanded
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Check if the accordion content has height (meaning it's expanded)
            // Don't check isIntersecting - we want to detect expansion even if element is below viewport
            if (entry.target.clientHeight > 0) {
              // Accordion is now expanded, perform scroll
              const BUFFER_MARGIN = 8;
              const HEADER_OFFSET_HEIGHT = appHeaderHeight + topNavHeight + BUFFER_MARGIN;
              const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
              const offsetPosition = elementPosition - HEADER_OFFSET_HEIGHT;

              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
              });

              // Clean up observer
              if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
              }
            }
          });
        },
        {
          threshold: 0,
          root: null,
        },
      );

      // Start observing the accordion content
      observerRef.current.observe(accordionContent);
    },
    [parties],
  );

  const handleExpandProperties = (isExpand: boolean) => {
    const tempProperty =
      properties?.map((propertyValue) => {
        propertyValue.expanded = isExpand;
        return propertyValue;
      }) ?? [];
    setProperties([...tempProperty]);
  };

  const handleExpandSingleProperty = (isExpand: boolean, id: string | number) => {
    const tempProperty =
      properties?.map((propertyValue, index) => {
        if (index === id) {
          propertyValue.expanded = isExpand;
        }
        return propertyValue;
      }) ?? [];
    setProperties([...tempProperty]);
  };

  const handleExpandParties = (isExpand: boolean) => {
    const tempParty =
      parties?.map((partyValue) => {
        partyValue.expanded = isExpand;
        return partyValue;
      }) ?? [];
    setParties([...tempParty]);
  };

  const handleExpandSingleParty = (isExpand: boolean, id: string | number) => {
    const tempParty =
      parties?.map((partyValue, index) => {
        if (index === id) {
          partyValue.expanded = isExpand;
        }
        return partyValue;
      }) ?? [];
    setParties([...tempParty]);
  };

  const propertySubtitle = (property: Property) => {
    const subtitle = [property.propertyType];
    if (property.apn) {
      subtitle.push(`APN: ${property.apn}`);
    }
    if (property.address?.county) {
      subtitle.push(`${capitalizeFirstLetter(property.address.county)} County`);
    }
    return subtitle;
  };

  const propertySeverity = (property: Property) => {
    const visibleRisks = showFincenInOI?.value
      ? property.risks
      : property.risks?.filter((r) => r.categoryName !== 'FinCen');
    let severity;
    if (visibleRisks?.some((x) => x.categoryName === 'Property' && x.typeName === 'MissingData')) {
      severity = 'Alert';
    } else {
      severity = getParentSeverity(visibleRisks ?? [], RiskCategory.Property);
      if (severity === 'None' || severity === 'Error') {
        severity = 'Warning';
      }
    }
    return severity;
  };

  const getTimeDifference = (pastDate: Date) => {
    const now = new Date();
    const diffInMs = now.getTime() - pastDate.getTime();

    const seconds = Math.floor(diffInMs / 1000);
    const minutes = Math.floor(diffInMs / (1000 * 60));
    const hours = Math.floor(diffInMs / (1000 * 60 * 60));
    const days = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (seconds < 60) {
      return 'Just Now';
    } else if (minutes < 60) {
      return `${minutes} Minutes Ago`;
    } else if (hours < 24) {
      return `${hours} Hours Ago`;
    } else if (days < 7) {
      return `${days} Days Ago`;
    } else if (days < 22) {
      const weeks = Math.floor(days / 7);
      return `${weeks} Weeks Ago`;
    } else {
      const formattedDate = pastDate.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
      });
      return formattedDate;
    }
  };

  async function handleUpdateEmailPreferences(data: OrderInsightsEmailPreference) {
    setUpdateEmailPreferenceInProgress(true);

    const payload = {
      firmId: data.firmId,
      emailSubscription: {
        isEnabled: data.isEnabled,
        emailFor: data.emailFor,
        notificationTypes: data.notificationTypes,
      },
    };

    const token = await getAccessToken();
    const response = await UpdateOrderInsightsEmailPreference(payload, token).catch((error) => {
      console.error('Error updating order insights email preferences: ', error);
      addGlobalMsg({
        message: 'System Error. Please try again later',
        type: 'error',
      });
    });

    setUpdateEmailPreferenceInProgress(false);
    if (response && response > 0) {
      setEmailPreferenceDrawerData(data.isEnabled ? data : defaultEmailPreference);
    }
  }

  const downloadReportPdf = async () => {
    setDownloadingSummary(true);
    addSnackbarMessage({
      message: `Generating File Summary Report...`,
      type: 'success',
    });

    const token = await getAccessToken();
    await generateFileSummaryReport(
      fileData?.fileId ?? '',
      fileData?.fileNumber ?? '',
      isFraudValidationDisabled ? '' : userFirm?.firmId ?? '',
      token,
      addGlobalMsg,
    );

    setDownloadingSummary(false);
  };

  const handleCloseDrawer = () => {
    setEmailPreferenceDrawer(false);
  };

  // #endregion
  useEffect(() => {
    if (!userFirm?.firmId) {
      history.push('/err');
    } else {
      executeGetEmailPreference().then();
    }
  }, [userFirm]);

  useEffect(() => {
    if (fileId) {
      setRouterParamValue(fileId);
      setActionInProgress('pending');
      loadFileOrderInsightsData && loadFileOrderInsightsData(fileId, true);
      if (fileData?.fileId === '' || fileData?.fileId !== fileId) {
        loadFileData && loadFileData({ fileId: fileId });
      }
    }
  }, [fileId]);

  useEffect(() => {
    if (fileRefreshTrigger && fileId) {
      loadFileOrderInsightsData?.(fileId, true);
      triggerFileRefresh?.(false);
    }
  }, [fileRefreshTrigger]);

  useEffect(() => {
    if (emailPreferenceData?.isEnabled) {
      setEmailPreferenceDrawerData({ ...emailPreferenceData, firmId: userFirm?.firmId ?? '' });
    } else {
      setEmailPreferenceDrawerData({ ...defaultEmailPreference, firmId: userFirm?.firmId ?? '' });
    }
  }, [emailPreferenceData]);

  useEffect(() => {
    if (fileOrderInsightsData?.fileId !== fileId) {
      return;
    }

    if (fileOrderInsightsData?.hasError) {
      setActionInProgress('');
    }
    const lastDtUpdate = fileOrderInsightsData?.lastGeneratedDate;
    if (lastDtUpdate && !fileOrderInsightsData.isGenerating) {
      const timeDifference = getTimeDifference(new Date(lastDtUpdate));
      setLastUpdated(timeDifference);
    }
    if (fileOrderInsightsData?.properties) {
      const filePartyType =
        fileOrderInsightsData?.parties.filter((party) => party.filePartyType === 'Seller').length > 0
          ? 'Seller'
          : 'Buyer';
      const mappedProperties = fileOrderInsightsData.properties.map((property) => {
        return {
          ...property,
          onFileOwnerAddressWithPartyName: onFileOwnerAddresses(fileOrderInsightsData?.parties, filePartyType),
          onFileOwnerNames: onFileOwnerNames(fileOrderInsightsData?.parties, filePartyType),
          expanded: false,
        };
      });
      setProperties(mappedProperties);
    }
    if (fileOrderInsightsData?.jackets) {
      const policyLiabilityJacket = fileOrderInsightsData.jackets.find((jacket) => jacket.isMaxLiabilityAmount);
      setPolicyLiabilityJacket(policyLiabilityJacket);
    }
    if (fileOrderInsightsData?.parties && !fileOrderInsightsData.isGenerating) {
      const queryParams = new URLSearchParams(location.search);
      const openPartyId = queryParams.get('openParty');

      const mappedParties = fileOrderInsightsData.parties.map((party) => {
        const shouldExpand = openPartyId && String(party.id) === openPartyId;
        return { ...party, expanded: shouldExpand || false };
      });
      setParties(mappedParties);

      // If we expanded a party via query params, mark it for scroll and clean up URL
      if (openPartyId) {
        setPendingScrollPartyId(openPartyId);
        window.history.replaceState({}, '', location.pathname);
      }
    }
  }, [fileOrderInsightsData]);

  // Separate effect to handle scroll after parties are set with expanded state
  useEffect(() => {
    if (pendingScrollPartyId && parties) {
      scrollToPartyByRef(pendingScrollPartyId);
      setPendingScrollPartyId(null);
    }
  }, [pendingScrollPartyId, parties, scrollToPartyByRef]);

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      setActionInProgress(isDiscoveringOrderInsights ?? '');
    }
    return () => {
      isMounted = false;
    };
  }, [isDiscoveringOrderInsights]);

  // Cleanup on unmount - clear context data and observers
  useEffect(() => {
    return () => {
      setFileOrderInsightsData?.(null);
      setProperties(undefined);
      setParties(undefined);
      setPolicyLiabilityJacket(undefined);
      setLastUpdated('');
      setActionInProgress('');
      setPendingScrollPartyId(null);
      partiesSectionRef.current = {};
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (
      Array.isArray(refreshOrderInsightsError) &&
      refreshOrderInsightsError.length > 0 &&
      refreshOrderInsightsStatus === 'error'
    ) {
      setOrderInsightsError([...refreshOrderInsightsError]);
    }
  }, [refreshOrderInsightsError, refreshOrderInsightsStatus]);

  useEffect(() => {
    if (orderInsightsError.length > 0) showErrorMessage(orderInsightsError);
  }, [orderInsightsError]);

  useEffect(() => {
    if (refreshOrderInsightsData && loadFileOrderInsightsData) loadFileOrderInsightsData(fileId ?? '');
  }, [refreshOrderInsightsData]);

  const isPartiesExist = React.useMemo(() => parties && parties.length > 0, [parties]);

  const marketPlaceUnderwriterCode = useMemo(() => {
    if (fileData?.fileId !== fileId) {
      return undefined;
    }
    return fileData?.underwriterCode;
  }, [fileData?.fileId, fileData?.underwriterCode, fileId]);

  useEffect(() => {
    if (isMarketPlaceEnabled) fetchMarketPlaceServices(marketPlaceUnderwriterCode);
  }, [isMarketPlaceEnabled, marketPlaceUnderwriterCode]);

  useEffect(() => {
    if (fetchMarketPlaceServicesResults) {
      const marketPlaceResult: PartyOrderInsightsProps['enableStatus'] = {};
      const keyMap: { [key: string]: 'contactValidation' | 'enhancedIdentityVerification' } = {
        'Contact Validation': 'contactValidation',
        'Identity Verification': 'enhancedIdentityVerification',
      };
      fetchMarketPlaceServicesResults.map((result: MarketplaceProduct) => {
        if (keyMap[result.productName])
          marketPlaceResult[keyMap[result.productName]] = {
            enabled: result.isEnabled,
            productCode: result.productCode,
            description: result.description,
          };
      });
      setEnableStatus(marketPlaceResult);
    }
  }, [fetchMarketPlaceServicesResults]);

  return (
    <div className={classes.container}>
      <LoadingSpinner status={isActionInProgress} variant="linear" />
      <PageHeader
        title="Order Insights"
        subtitle="Real-time Order Insights, our proactive fraud alert system, validates available party and property characteristics, including watchlists, claims, vacant land, name mismatches, and more."
        contentRight={
          <div className={classes.flexGap}>
            <div className={classes.subtitleWrap}>
              {/* on hover */}
              {lastUpdated && isActionInProgress !== 'pending' && (
                <>
                  <OrderInsightUpdateButton
                    btnText={lastUpdated}
                    onMouseHoverBtnText={'Check For Updates'}
                    onClick={handleRefreshOrderInsights}
                    data-testid={'RefreshOrderInsights'}
                  ></OrderInsightUpdateButton>
                </>
              )}
            </div>
            {!lastUpdated && !fileOrderInsightsData?.hasError && isActionInProgress !== 'pending' && (
              <div className={classes.subtitleWrap}>
                <AgentNetButton
                  variant="text"
                  size="small"
                  onClick={handleRefreshOrderInsights}
                  data-testid={'RefreshOrderInsights'}
                >
                  Check For Updates
                </AgentNetButton>
              </div>
            )}
            {isActionInProgress === 'pending' && (
              <Typography
                variant="body2"
                style={{
                  color: '#0074CA',
                  fontWeight: 500,
                  fontSize: '13px',
                  display: 'flex',
                  margin: '1em 0',
                  whiteSpace: 'nowrap',
                }}
              >
                Discovering Insights
                <CircularProgress
                  style={{
                    marginLeft: '6px',
                  }}
                  size={15}
                  className="MuiAgentNetButton-Spinner"
                />
              </Typography>
            )}

            <div>
              <AgentNetButton
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => onEmailSubscribe()}
                disabled={emailPreferenceError?.length > 0 || emailPreferenceStatus == 'pending'}
                data-testid={
                  emailPreferenceDrawerData?.isEnabled
                    ? 'OrderInsightsManageEmailSubscribe'
                    : 'OrderInsightsEmailSubscribe'
                }
              >
                <>
                  {updateEmailPreferenceInProgress && (
                    <CircularProgress
                      style={{
                        marginRight: '6px',
                      }}
                      size={15}
                      className="MuiAgentNetButton-Spinner"
                    />
                  )}
                  {emailPreferenceDrawerData?.isEnabled ? 'Manage Email Subscription' : 'Subscribe to Email Updates'}
                </>
              </AgentNetButton>
            </div>
            <div>
              <AgentNetButton
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => downloadReportPdf()}
                disabled={downloadingSummary}
                data-testid="FileSummaryPDF"
                startIcon={
                  downloadingSummary ? (
                    <LoadingSpinner
                      className={classes.loader}
                      variant="circle"
                      status={'pending'}
                      showLabel={false}
                      size={16}
                    />
                  ) : (
                    <PDFIcon disabled={downloadingSummary} />
                  )
                }
              >
                File Summary
              </AgentNetButton>
            </div>
          </div>
        }
        menuItems={[
          {
            label: 'Knowledge Base',
            link: KBLinks.orderInsights,
          },
        ]}
        disableMargin="x"
      />

      {fileOrderInsightsData && fileOrderInsightsData.hasError && (
        <div className={classes.center}>
          <img src={EmptyImg} alt="contact-us" />
          <Typography variant="h3">Unable to Load Order Insights</Typography>
          <Typography component="span" variant="body2" className={classes.greyTxt}>
            Please try again later or contact the Agency Service Center (ASC) at 866-701-3361 or email{' '}
            <a href="mailto:AgencySupport@firstam.com" className={classes.link}>
              AgencySupport@firstam.com
            </a>
            .
          </Typography>
          <ContactUsDialog open={contactUsModal} onClose={() => setContactUsModal(false)} />
        </div>
      )}
      {fileOrderInsightsData && !fileOrderInsightsData.hasError && (
        <>
          <div className={classes.flex}>
            <AgentNetDivider
              title={'Properties'}
              typoVariant="h2"
              buttonName={'Close All'}
              buttonName2={'Expand All'}
              onClick={() => {
                handleExpandProperties(false);
              }}
              onClick2={() => {
                handleExpandProperties(true);
              }}
              buttonNameQaAttr="PropertiesCloseAll"
              buttonName2QaAttr="PropertiesExpandAll"
              disablePaddingX
            />
          </div>
          <div className={classes.flex}>
            {properties &&
              properties.map((property: Property, index: number) => {
                return (
                  <AccordionContent
                    key={index}
                    id={index}
                    title={toFullAddress(property.address)}
                    expanded={property.expanded}
                    subtitle={propertySubtitle(property)}
                    onExpandChange={handleExpandSingleProperty}
                    accordianQAAttribute={`OrderInsightPropertySummary${index}`}
                    icon="property"
                    type="property"
                    orderInsightsRiskSeverity={
                      isActionInProgress !== 'pending' && lastUpdated ? propertySeverity(property) : ''
                    }
                  >
                    {isActionInProgress !== 'pending' && lastUpdated ? (
                      <PropertyOrderInsights
                        property={property}
                        policyLiabilityJacket={policyLiabilityJacket}
                        isPdf={false}
                        index={index}
                        key={index}
                      />
                    ) : undefined}
                  </AccordionContent>
                );
              })}
          </div>
          <div className={classes.flex}>
            <AgentNetDivider
              title={'Parties'}
              typoVariant="h2"
              buttonName={isPartiesExist ? 'Close All' : ''}
              buttonName2={isPartiesExist ? 'Expand All' : ''}
              onClick={() => {
                handleExpandParties(false);
              }}
              onClick2={() => {
                handleExpandParties(true);
              }}
              buttonNameQaAttr="PartiesCloseAll"
              buttonName2QaAttr="PartiesExpandAll"
              disablePaddingX
            />
          </div>
          <div className={classes.flex}>
            {parties &&
              parties.map((party: Party, index: number) => {
                const hasValidationRisk = (party.contactValidation?.risks?.length ?? 0) > 0;
                const hasEnhancedRisk = (party.enhancedIdentifyVerification?.summary?.risks?.length ?? 0) > 0;
                const enhancedStatus = party.enhancedIdentifyVerification?.transactionStatus;
                const enhancedStatusRisk =
                  enhancedStatus !== undefined &&
                  [TransactionStatusEnum.Pending, TransactionStatusEnum.AwaitingUserAction].includes(enhancedStatus)
                    ? enhancedVerificationPendingRisk
                    : enhancedStatus === TransactionStatusEnum.Failed
                    ? enhancedVerificationFailedRisk
                    : null;
                const combinedRisks: Risk[] = [
                  ...(party.risks ?? []),
                  ...(hasValidationRisk || hasEnhancedRisk ? [validationAlertRisk] : []),
                  ...(enhancedStatusRisk ? [enhancedStatusRisk] : []),
                ];

                return (
                  <div ref={setPartyRef(party.id, party.key)} key={`accordian-parent-${party.key}`}>
                    <AccordionContent
                      key={`accordian-${party.key}`}
                      id={index}
                      title={party.partyName}
                      expanded={party.expanded}
                      subtitle={[filePartyTypeMap[party.filePartyType], party.partyType]}
                      onExpandChange={handleExpandSingleParty}
                      accordianQAAttribute={`OrderInsightPartySummary${index}`}
                      icon={party.partyType}
                      type={party.filePartyType.toLowerCase()}
                      orderInsightsRiskSeverity={
                        isActionInProgress !== 'pending' && lastUpdated
                          ? getParentSeverity(combinedRisks ?? [], RiskCategory.Party)
                          : ''
                      }
                    >
                      {isActionInProgress !== 'pending' && lastUpdated ? (
                        <PartyOrderInsights
                          party={party}
                          key={`parties-${party.key}`}
                          index={index}
                          isPdf={false}
                          enableStatus={enableStatus}
                        />
                      ) : undefined}
                    </AccordionContent>
                  </div>
                );
              })}
            {!isPartiesExist && <PartyNotCreated></PartyNotCreated>}
          </div>
          <div className={classes.disclaimer}>
            <Typography variant="h5" className={classes.lowerCase}>
              Disclaimer:
            </Typography>
          </div>
          <Typography variant="h6" className={classes.lowerCase2}>
            {disclaimerText}
          </Typography>
        </>
      )}
      <OrderInsightsEmailPreferenceDrawer
        open={emailPreferenceDrawer}
        onClose={handleCloseDrawer}
        drawerData={emailPreferenceDrawerData}
        onUpdateEmailPreferences={handleUpdateEmailPreferences}
        isActionInProgress={updateEmailPreferenceInProgress}
      />
    </div>
  );
}
